import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import { useAxios } from '../../utilities/Requests/useAxios'
import { getImageUrl } from '../../utilities/Storage'
import {
  sqsMessage,
  WebSocketContext,
  WebSocketContextInterface,
} from '../WebSocket/WebSocketProvider'
import {
  MessageData,
  MessagesContextType,
  MessageThreadData,
  MessageUser,
  SendMessageData,
  ThreadsKey,
  ThreadsObj,
} from './types'

export const MessagesContext = createContext<MessagesContextType | null>(null)

export const MessagesProvider = ({ children }: { children?: ReactNode }) => {
  const { fetch } = useAxios()

  //websocket
  const { isConnected, sqsMessages } = useContext(
    WebSocketContext
  ) as WebSocketContextInterface

  const [isNewMessage, setIsNewMessage] = useState(false)

  const [navigatingDirectlyToThread, setNavigatingDirectlyToThread] =
    useState(false)

  const [threads, setThreads] = useState<ThreadsObj>({
    myPatients: [],
    providers: [],
    otherPatients: [],
    pastPatients: [],
  })

  const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null)

  // attachments
  const [attachmentKeys, setAttachmentKeys] = useState<
    { id: number; key: string }[]
  >([])

  useEffect(() => {
    if (isConnected) {
      getProviderMessageThreads()
    }
  }, [isConnected])

  useEffect(() => {
    if (sqsMessages.length > 0 && threads.myPatients.length) {
      updateThreadsFromWebSocket(sqsMessages)
    }
  }, [sqsMessages])

  const addMessageToThread = async (newMessage: sqsMessage) => {
    const threadData = getCategoryThreadAndIndexFromState(
      threads,
      newMessage.threadId
    )

    if (threadData.indexInCategory === -1) {
      await getProviderMessageThreads()
      return
    }

    setThreads((prev) => {
      const threadData = getCategoryThreadAndIndexFromState(
        prev,
        newMessage.threadId
      )

      if (threadData.indexInCategory === -1) {
        return prev
      }

      const isMessageInThread = checkIfSQSMessageIsInThread(
        newMessage,
        threadData.thread.messages
      )

      if (isMessageInThread) {
        return prev
      }

      const isLatestMessage = checkIfMostRecentMessageInThread(
        newMessage,
        threadData.thread.messages
      )

      const updatedThread = {
        ...threadData.thread,
        unread: isLatestMessage,
        messages: [
          {
            messageId: newMessage.messageId,
            messageBody: newMessage.messageBody,
            fromUser: {
              id: newMessage.fromUser.id,
              name: newMessage.fromUser.name,
              role: newMessage.fromUser.role,
              photoUrl: newMessage.fromUser.photoUrl,
              isActive: newMessage.fromUser.isActive,
              isPatient: newMessage.fromUser.isPatient,
            } as unknown as MessageUser,
            toUsers: newMessage.toUsers?.map((u) => u),
            receivedDateTime: newMessage.receivedDateTime,
            urgent: false,
            attachments: newMessage.attachments ? newMessage.attachments : [],
            isFirstMessageInThread: false,
          },
          ...threadData.thread.messages,
        ],
      }

      const updatedCategory = [...prev[threadData.categoryName]]
      updatedCategory[threadData.indexInCategory] = updatedThread

      const mergedThreads = {
        ...prev,
        [threadData.categoryName]: updatedCategory,
      }

      Object.keys(mergedThreads).forEach((key) => {
        if (mergedThreads[key as ThreadsKey] !== prev[key as ThreadsKey]) {
          mergedThreads[key as ThreadsKey] = sortThreadsByLatestMessage(
            mergedThreads[key as ThreadsKey]
          )
        }
      })

      return mergedThreads
    })
  }

  const sortThreadsByLatestMessage = (threadsToSort: MessageThreadData[]) => {
    return [...threadsToSort].sort((a, b) => {
      const aDate = a.messages[0]?.receivedDateTime
      const bDate = b.messages[0]?.receivedDateTime
      if (!aDate) return 1
      if (!bDate) return -1
      return new Date(bDate).getTime() - new Date(aDate).getTime()
    })
  }

  const updateThreads = (updatedThreads: ThreadsObj) => {
    setThreads((prev) => {
      const isThreadsEmpty = checkAllThreadsEmpty(prev)

      if (isThreadsEmpty) {
        return updatedThreads
      }

      const result = { ...prev }

      Object.entries(updatedThreads).forEach(
        ([categoryName, category]: [string, MessageThreadData[]]) => {
          category.forEach((newThread) => {
            const currentThreadData = getCategoryThreadAndIndexFromState(
              result,
              newThread.messageThreadId
            )

            if (currentThreadData.indexInCategory === -1) {
              // Thread doesn't exist yet — add it to the correct category
              if (
                result[categoryName as ThreadsKey] ===
                prev[categoryName as ThreadsKey]
              ) {
                result[categoryName as ThreadsKey] = [
                  ...prev[categoryName as ThreadsKey],
                ]
              }
              result[categoryName as ThreadsKey].push(newThread)
              return
            }

            const mergedThread = mergeThreadDataWithExistingThread(
              currentThreadData.thread,
              newThread,
              'getProviderMessageThreads'
            )

            if (
              result[currentThreadData.categoryName] ===
              prev[currentThreadData.categoryName]
            ) {
              result[currentThreadData.categoryName] = [
                ...prev[currentThreadData.categoryName],
              ]
            }

            result[currentThreadData.categoryName][
              currentThreadData.indexInCategory
            ] = mergedThread
          })
        }
      )
      // Sort each category by latest message
      Object.keys(result).forEach((key) => {
        if (result[key as ThreadsKey] !== prev[key as ThreadsKey]) {
          result[key as ThreadsKey] = sortThreadsByLatestMessage(
            result[key as ThreadsKey]
          )
        }
      })

      return result
    })
  }

  const updateThread = (newThread: MessageThreadData, updatingFrom: string) => {
    setThreads((prev) => {
      const currentThreadData = getCategoryThreadAndIndexFromState(
        prev,
        newThread.messageThreadId
      )

      if (currentThreadData.indexInCategory === -1) {
        return prev
      }

      const mergedThread = mergeThreadDataWithExistingThread(
        currentThreadData.thread,
        newThread,
        updatingFrom
      )

      const updatedCategory = [...prev[currentThreadData.categoryName]]
      updatedCategory[currentThreadData.indexInCategory] = mergedThread

      return {
        ...prev,
        [currentThreadData.categoryName]: updatedCategory,
      }
    })
  }

  const updateThreadsFromWebSocket = (newMessages: sqsMessage[]) => {
    newMessages.forEach((message) => addMessageToThread(message))
  }

  const mergeThreadDataWithExistingThread = (
    currentThread: MessageThreadData,
    newThread: MessageThreadData,
    updatingFrom: string
  ) => {
    const mergedThreads = {
      ...currentThread,
      unread:
        updatingFrom === 'getProviderMessageThreads' ? newThread.unread : false,
      displayUser:
        updatingFrom === 'getProviderMessageThreads'
          ? newThread.displayUser
          : currentThread.displayUser,
      displayThreadUserNames:
        updatingFrom === 'getProviderMessageThreads'
          ? newThread.displayThreadUserNames
          : currentThread.displayThreadUserNames,
      displayUserCounts:
        updatingFrom === 'getProviderMessageThreads'
          ? newThread.displayUserCount
          : currentThread.displayUserCount,
      otherUsers:
        updatingFrom === 'getProviderMessageThreads'
          ? newThread.otherUsers
          : currentThread.otherUsers,
      messages: currentThread
        ? deduplicateMessagesAndSortByDate(
            currentThread.messages,
            newThread.messages
          )
        : newThread.messages,
    }
    return mergedThreads
  }
  const getProviderMessageThreads = async () => {
    const { data, error } = await fetch({
      path: `Message/GetProviderMessageThreads`,
    })
    if (data) {
      updateThreads(data)
      return data
    } else if (error) {
      throw new Error(`Error in getProviderMessageThreads.`)
    }
  }

  const getProviderPastPatientMessageThreads = async () => {
    const { data, error } = await fetch({
      path: `Message/GetProviderPastPatientMessageThreads`,
    })
    if (data) {
      data.forEach(async (d: MessageThreadData) => {
        if (d.displayUser.photoUrl) {
          d.displayUser.photoUrl = await getImageUrl(d.displayUser.photoUrl)
        }

        d.messages.forEach(async (m: MessageData) => {
          if (m.fromUser.photoUrl) {
            m.fromUser.photoUrl = await getImageUrl(m.fromUser.photoUrl)
          }
          m.toUsers.forEach(async (t: MessageUser) => {
            if (t.photoUrl) {
              t.photoUrl = await getImageUrl(t.photoUrl)
            }
          })
        })
      })

      setThreads((prev) => {
        return { ...prev, pastPatients: data }
      })

      return data
    } else if (error) {
      throw new Error(`Error in getProviderMessageThreads.`)
    }
  }

  // get all the messages in one thread
  const getProviderMessagesInThread = async (
    threadId: number,
    messageId: number
  ) => {
    const isThreadsEmpty = checkAllThreadsEmpty(threads)

    if (isThreadsEmpty) {
      await getProviderMessageThreads()
    }

    const { data, error } = await fetch({
      path: `Message/GetProviderMessagesInThread?messageThreadId=${threadId}&messageId=${messageId}`,
    })
    if (data) {
      updateThread(data, 'getProviderMessagesInThread')
      return { ...data }
    } else if (error) {
      throw new Error(`Error in getProviderMessagesInThread.`)
    }
  }

  const checkIfThreadExists = async (patientId: number) => {
    const { data, error } = await fetch({
      path: `Message/CheckIfThreadExistsProvider?patientId=${patientId}`,
      methodType: 'GET',
    })

    return data
  }

  const sendMessage = async (sendMessageData: SendMessageData) => {
    const { error } = await fetch({
      path: `Message/SendProviderMessage`,
      methodType: 'POST',
      body: sendMessageData,
    })

    console.log('🚀 ~ sendMessage ~ sendMessageData', sendMessageData)

    if (!isConnected && sendMessageData.messageThreadId !== undefined) {
      await getProviderMessagesInThread(sendMessageData.messageThreadId, 0)
    }

    if (!isConnected && sendMessageData.messageThreadId === undefined) {
      await getProviderMessageThreads()
    }

    if (error) {
      throw new Error(`Error in sendMessage.`)
    }
  }

  const getCurrentThread = () => {
    const threadsCopy = { ...threads }
    let currentThread = undefined
    Object.keys(threadsCopy).forEach((category) => {
      const categoryThreads = threadsCopy[category as keyof ThreadsObj]
      const thread = categoryThreads.find(
        (thread) => thread.messageThreadId === selectedThreadId
      )
      if (thread) {
        currentThread = thread
        return
      }
      return
    })
    return currentThread
  }

  const checkIfSQSMessageIsInThread = (
    newMessage: sqsMessage,
    messages: MessageData[]
  ) => {
    return messages.some(
      (message: MessageData) => message.messageId === newMessage.messageId
    )
  }

  const deduplicateMessagesAndSortByDate = (
    existingMessages: MessageData[],
    newMessages: MessageData[]
  ) => {
    const existingIds = new Set(existingMessages.map((msg) => msg.messageId))
    const uniqueNewMessages = newMessages.filter(
      (msg) => !existingIds.has(msg.messageId)
    )
    return [...existingMessages, ...uniqueNewMessages].sort(
      (a, b) =>
        new Date(b.receivedDateTime).getTime() -
        new Date(a.receivedDateTime).getTime()
    )
  }

  const checkIfMostRecentMessageInThread = (
    newMessage: sqsMessage,
    messages: MessageData[]
  ) => {
    const isMsgMostRecentMessage = messages.every(
      (message) =>
        new Date(message.receivedDateTime) <
        new Date(newMessage.receivedDateTime)
    )
    return isMsgMostRecentMessage
  }

  const getCategoryThreadAndIndexFromState = (
    threadsState: ThreadsObj,
    threadId: number
  ) => {
    for (const [categoryName, category] of Object.entries(threadsState)) {
      const indexInCategory = category.findIndex(
        (thread: MessageThreadData) => thread.messageThreadId === threadId
      )
      if (indexInCategory !== -1) {
        const thread = category[indexInCategory]
        return {
          categoryName: categoryName as ThreadsKey,
          thread,
          indexInCategory,
        }
      }
    }
    return {
      categoryName: '' as ThreadsKey,
      thread: {} as MessageThreadData,
      indexInCategory: -1,
    }
  }

  const checkAllThreadsEmpty = (threadsState: ThreadsObj) => {
    return Object.values(threadsState).every(
      (threadCategory) => threadCategory.length === 0
    )
  }

  const currentThread = getCurrentThread()

  useEffect(() => {
    console.log('currentThread: ', currentThread)
  }, [currentThread])

  useEffect(() => {
    console.log('selectedThreadId: ', selectedThreadId)
  }, [selectedThreadId])

  return (
    <MessagesContext.Provider
      value={{
        threads,
        currentThread,
        getProviderMessageThreads,
        selectedThreadId,
        setSelectedThreadId,
        getProviderMessagesInThread,
        getProviderPastPatientMessageThreads,
        checkIfThreadExists,
        sendMessage,
        attachmentKeys,
        setAttachmentKeys,
        isNewMessage,
        setIsNewMessage,
        navigatingDirectlyToThread,
        setNavigatingDirectlyToThread,
      }}
    >
      {children}
    </MessagesContext.Provider>
  )
}

export const useMessages = () =>
  useContext(MessagesContext) as MessagesContextType
